import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Scissors, Loader2, AlertTriangle, ListVideo, PlayCircle, Flame, HeartHandshake, BookOpen, Church, Share2, Sparkles, Search, X } from "lucide-react";
import { getPublicShow, publicSearch, mediaUrl, streamUrl, shareLink } from "../lib/api";
import { toast } from "sonner";

const ts = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

function ShareBtn({ url, label }) {
  const doShare = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const full = url.startsWith("http") ? url : window.location.origin + url;
    try { if (navigator.share) { await navigator.share({ url: full, title: label }); return; } } catch { /* fall through */ }
    try { await navigator.clipboard.writeText(full); toast.success("Share link copied!"); }
    catch { window.prompt("Copy this link:", full); }
  };
  return (
    <button data-testid="share-moment-btn" onClick={doShare}
            className="ml-auto inline-flex shrink-0 items-center gap-1 font-mono text-[11px] text-accent hover:text-white">
      <Share2 className="h-3.5 w-3.5" /> Share
    </button>
  );
}

export default function PublicShow() {
  const { showId } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const playerRef = useRef(null);
  const startAt = Number(new URLSearchParams(window.location.search).get("t")) || 0;
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    const query = q.trim();
    if (!query) { setResults(null); return; }
    setSearching(true);
    try {
      const handle = data?.creator?.handle;
      const d = await publicSearch(handle, { q: query });
      setResults((d.clips || []).filter((c) => c.show_id === showId).concat((d.clips || []).filter((c) => c.show_id !== showId)));
    } catch { setResults([]); }
    setSearching(false);
  };

  useEffect(() => {
    getPublicShow(showId).then((d) => { setData(d); setState("ready"); }).catch(() => setState("error"));
  }, [showId]);

  const seek = (sec) => {
    const p = playerRef.current;
    if (p) { p.currentTime = sec; p.play?.(); window.scrollTo({ top: 0, behavior: "smooth" }); }
  };

  if (state === "loading") return <div className="grid h-screen place-items-center bg-background text-accent"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (state === "error") return (
    <div className="grid h-screen place-items-center bg-background text-center text-white">
      <div><AlertTriangle className="mx-auto h-8 w-8 text-primary" /><p className="mt-4 font-display text-2xl">Show not available</p>
        <a href="/demo" className="mt-6 inline-block bg-accent px-6 py-3 font-body text-sm font-semibold text-black">See the demo</a></div>
    </div>
  );

  const { show, creator, clips } = data;
  return (
    <div className="min-h-screen bg-background text-white" data-testid="public-show-page">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <a href="/demo" className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center bg-accent"><Scissors className="h-4 w-4 text-black" /></div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-accent">Clip Miner</span>
          </a>
          {creator?.handle && <Link to={`/u/${creator.handle}`} className="font-mono text-xs text-accent hover:underline">@{creator.handle}</Link>}
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-8">
        <h1 className="font-display text-4xl tracking-wide" data-testid="show-title">{show.title}</h1>
        <p className="mt-1 font-mono text-xs text-muted-foreground">{ts(show.duration_sec)} · {clips.length} clips
          {show.topics?.length > 0 && ` · ${show.topics.slice(0, 6).join(", ")}`}</p>

        {/* Natural-language "clip what you describe" search */}
        <form onSubmit={runSearch} className="mt-6" data-testid="show-search-form">
          <div className="flex items-center gap-2 border border-accent/40 bg-accent/[0.04] px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-accent" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="show-search-input"
                   placeholder="Describe a moment — e.g. every time Barth talks about relationships"
                   className="min-w-0 flex-1 bg-transparent font-body text-sm text-white outline-none placeholder:text-muted-foreground" />
            {q && <button type="button" data-testid="show-search-clear" onClick={() => { setQ(""); setResults(null); }}
                          className="text-muted-foreground hover:text-white"><X className="h-4 w-4" /></button>}
            <button type="submit" data-testid="show-search-btn" disabled={searching}
                    className="shrink-0 bg-accent px-3 py-1.5 font-body text-xs font-semibold text-black">
              {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Find clips"}
            </button>
          </div>
        </form>

        {results !== null && (
          <section className="mt-5" data-testid="show-search-results">
            <p className="font-mono text-xs text-muted-foreground">
              {results.length} matching clip{results.length === 1 ? "" : "s"} for “{q}”
            </p>
            {results.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {results.map((clip) => (
                  <Link key={clip.id} to={`/c/${clip.id}`} data-testid={`search-result-${clip.id}`}
                        className="group relative block overflow-hidden border border-border bg-black transition-transform hover:-translate-y-1 hover:border-accent">
                    <div className="relative aspect-[9/16] w-full bg-neutral-900">
                      {clip.poster ? <img src={mediaUrl(clip.poster)} alt={clip.title} loading="lazy" className="h-full w-full object-cover" />
                        : <><img src="/barth-tile.jpg" alt={clip.title} loading="lazy" className="h-full w-full object-cover" /><div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-2 pt-6 text-center font-display text-[11px] text-white">{clip.title}</div></>}
                      <span className="absolute left-2 top-2 flex items-center gap-1 bg-accent px-1.5 py-0.5 font-mono text-[10px] font-bold text-black"><Flame className="h-2.5 w-2.5" /> {clip.funny_score}</span>
                    </div>
                    <p className="line-clamp-2 p-2.5 font-body text-xs">{clip.title}</p>
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Full-episode player */}
        {show.source_path && (
          show.is_video ? (
            <video ref={playerRef} src={streamUrl(show.stream_path || show.source_path)} controls playsInline preload="metadata"
                   onLoadedMetadata={() => startAt && seek(startAt)}
                   className="mt-5 max-h-[60vh] w-full border border-border bg-black" data-testid="episode-player" />
          ) : (
            <audio ref={playerRef} src={streamUrl(show.stream_path || show.source_path)} controls preload="metadata"
                   onLoadedMetadata={() => startAt && seek(startAt)}
                   className="mt-5 w-full" data-testid="episode-player" />
          )
        )}

        {/* Chapters */}
        {show.chapters?.length > 0 && (
          <section className="mt-8">
            <h2 className="flex items-center gap-2 font-display text-2xl tracking-wide"><ListVideo className="h-5 w-5 text-accent" /> Chapters</h2>
            <div className="mt-3 divide-y divide-border border border-border" data-testid="public-chapters">
              {show.chapters.map((c, i) => (
                <button key={i} data-testid={`chapter-${i}`} onClick={() => seek(c.start)}
                        className="flex w-full items-start gap-4 px-4 py-3 text-left transition-colors hover:bg-accent/[0.06]">
                  <span className="mt-0.5 shrink-0 font-mono text-sm text-accent">{ts(c.start)}</span>
                  <span className="min-w-0">
                    <span className="block font-body text-sm font-semibold">{c.title}</span>
                    {c.caption && <span className="block font-body text-xs text-muted-foreground">{c.caption}</span>}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Care / Prayer catalog */}
        {show.care?.length > 0 && (
          <section className="mt-10">
            <h2 className="flex items-center gap-2 font-display text-2xl tracking-wide"><HeartHandshake className="h-5 w-5 text-primary" /> Prayer &amp; Care</h2>
            <p className="mt-1 font-body text-xs text-muted-foreground">People who shared what they're walking through — tap to hear their moment.</p>
            <div className="mt-3 space-y-2" data-testid="public-care">
              {show.care.map((e, i) => (
                <button key={e.id || i} data-testid={`care-${i}`} onClick={() => seek(e.start)}
                        className="block w-full border border-border px-4 py-3 text-left transition-colors hover:border-primary hover:bg-primary/[0.05]">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-accent">{ts(e.start)}</span>
                    <span className="font-body text-sm font-semibold">{e.person || "Anonymous"}</span>
                    {e.topic && <span className="bg-primary/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-primary">{e.topic}</span>}
                  </div>
                  {e.summary && <p className="mt-1 font-body text-xs text-muted-foreground">{e.summary}</p>}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Barth's stories from his past */}
        {show.story_moments?.length > 0 && (
          <section className="mt-10">
            <h2 className="flex items-center gap-2 font-display text-2xl tracking-wide"><BookOpen className="h-5 w-5 text-accent" /> Barth's Stories</h2>
            <p className="mt-1 font-body text-xs text-muted-foreground">Stories from Barth's own life — tap to jump right to them.</p>
            <div className="mt-3 space-y-2" data-testid="public-stories">
              {show.story_moments.map((e, i) => (
                <div key={e.id || i} className="flex items-start gap-2 border border-border px-4 py-3 transition-colors hover:border-accent hover:bg-accent/[0.06]">
                  <button data-testid={`story-${i}`} onClick={() => seek(e.start)} className="min-w-0 flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-accent">{ts(e.start)}</span>
                      <span className="font-body text-sm font-semibold">{e.title}</span>
                    </div>
                    {e.caption && <p className="mt-1 font-body text-xs text-muted-foreground">{e.caption}</p>}
                  </button>
                  <ShareBtn url={shareLink.show(show.id, e.start)} label={e.title} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Faith / Jesus moments */}
        {show.faith_moments?.length > 0 && (
          <section className="mt-10">
            <h2 className="flex items-center gap-2 font-display text-2xl tracking-wide"><Church className="h-5 w-5 text-accent" /> Faith &amp; Jesus</h2>
            <p className="mt-1 font-body text-xs text-muted-foreground">Every moment Barth talks about his faith — tap to listen.</p>
            <div className="mt-3 space-y-2" data-testid="public-faith">
              {show.faith_moments.map((e, i) => (
                <div key={e.id || i} className="flex items-start gap-2 border border-border px-4 py-3 transition-colors hover:border-accent hover:bg-accent/[0.06]">
                  <button data-testid={`faith-${i}`} onClick={() => seek(e.start)} className="min-w-0 flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-accent">{ts(e.start)}</span>
                      <span className="font-body text-sm font-semibold">{e.title}</span>
                    </div>
                    {e.caption && <p className="mt-1 font-body text-xs text-muted-foreground">{e.caption}</p>}
                  </button>
                  <ShareBtn url={shareLink.show(show.id, e.start)} label={e.title} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Clips */}
        {clips.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-2xl tracking-wide">Clips</h2>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {clips.map((clip) => (
                <Link key={clip.id} to={`/c/${clip.id}`} className="group relative block overflow-hidden border border-border bg-black transition-transform hover:-translate-y-1 hover:border-accent">
                  <div className="relative aspect-[9/16] w-full bg-neutral-900">
                    {clip.poster ? <img src={mediaUrl(clip.poster)} alt={clip.title} loading="lazy" className="h-full w-full object-cover" />
                      : <><img src="/barth-tile.jpg" alt={clip.title} loading="lazy" className="h-full w-full object-cover" /><div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-2 pt-6 text-center font-display text-[11px] text-white">{clip.title}</div></>}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100"><PlayCircle className="h-9 w-9 text-accent" /></div>
                    <span className="absolute left-2 top-2 flex items-center gap-1 bg-accent px-1.5 py-0.5 font-mono text-[10px] font-bold text-black"><Flame className="h-2.5 w-2.5" /> {clip.funny_score}</span>
                  </div>
                  <p className="line-clamp-2 p-2.5 font-body text-xs">{clip.title}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-10 border border-accent/30 bg-accent/[0.05] p-6 text-center">
          <p className="font-display text-xl">Made with Clip Miner</p>
          <a href="/" className="mt-4 inline-block bg-accent px-6 py-3 font-body text-sm font-semibold text-black">Start free</a>
        </div>
      </div>
    </div>
  );
}
